import { useEffect, useRef, useState, Fragment } from 'react'
import {
	Listbox,
	ListboxOption,
	ListboxOptions,
	Transition,
} from '@headlessui/react'
import { ContentTileWithoutImageFallback } from '../content/ContentTile'
import { CloseIcon, SearchIcon } from '../icons'
import fallbackAvatarImage from '#/images/fallbackAvatar.webp'
import clsx from 'clsx'
import {
	TypographyCaps,
	TypographyLabel,
	TypographyParagraph,
} from '../typography'

export interface UserType {
	id: string
	username: string
	address?: string
	addressArray?: string[]
	avatar?: string
}

const UserAccountLookupInput = ({
	name,
	value,
	users,
	setValue,
	handleUnsetSelected,
	placeholder,
	isInactive,
	showShadow = true,
	additionalClasses = '',
	containerWidth = 'w-80',
}: {
	name?: string
	value: UserType | undefined
	setValue: (user: UserType) => void
	handleUnsetSelected: (val: undefined) => void
	users: UserType[]
	placeholder: string
	isInactive: boolean
	showShadow?: boolean
	additionalClasses?: string
	containerWidth?: string
}) => {
	const [searchTerm, setSearchTerm] = useState('')
	const [isOpen, setIsOpen] = useState(false)
	const componentRef = useRef(null)
	const filteredOptions =
		searchTerm.length > 0
			? users
					.filter(user => {
						if (!user.username) {
							return false
						}
						if (user.username.toLowerCase().includes(searchTerm)) {
							return true
						}
						if (user.addressArray) {
							const matchingAddress = user.addressArray.find(address =>
								address?.toLowerCase()?.includes(searchTerm.toLowerCase()),
							)
							if (matchingAddress) {
								return true
							} else {
								return false
							}
						}
						return false
					})
					.map(user => {
						if (user.addressArray) {
							const matchingAddress = user.addressArray.find(address =>
								address?.toLowerCase()?.includes(searchTerm.toLowerCase()),
							)
							if (matchingAddress) {
								return {
									...user,
									address: matchingAddress,
								}
							} else {
								return user
							}
						} else {
							return user
						}
					})
			: users.filter(user => user.username)

	useEffect(() => {
		function handleClickOutside(event: any) {
			if (
				componentRef.current &&
				!componentRef.current.contains(event.target)
			) {
				// Click occurred outside the component
				setIsOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	return (
		<div className={`${containerWidth}`} ref={componentRef}>
			{name ? (
				<div className="w-full">
					<label className="mb-1 block leading-[14px] text-grey400 dark:text-grey500">
						<TypographyCaps size="small">{name}</TypographyCaps>
					</label>
				</div>
			) : null}
			<Listbox
				value={value}
				onChange={(val: UserType) => {
					setValue(val)
					setSearchTerm('')
					setIsOpen(false)
				}}
			>
				<div className={clsx('relative rounded-md dark:drop-shadow-none')}>
					<input
						type="text"
						value={searchTerm}
						onFocus={e => {
							if (!isOpen) setIsOpen(true)
						}}
						onChange={e => {
							if (!isOpen) setIsOpen(true)
							if (e.target.value) {
								setSearchTerm(e.target.value)
							} else if (!e.target.value) {
								setSearchTerm('')
							}
						}}
						name={name}
						id={name}
						disabled={isInactive}
						className={clsx(
							'block w-full rounded-md border-0 px-4 py-3 font-abc text-paragraphMedium outline-0 ring-1 ring-inset',
							// DROP SHADOW
							// 'drop-shadow-menu dark:drop-shadow-none',
							// Default
							'text-textPrimaryLight ring-textInactiveLight dark:text-textPrimaryDark dark:ring-textInactiveDark',
							// Inactive
							isInactive &&
								'cursor-not-allowed truncate bg-uiSecondaryLight text-textPrimaryLight placeholder:text-textSecondaryLight dark:bg-black dark:text-textPrimaryDark dark:placeholder:text-textSecondaryDark',
							!isInactive &&
								'cursor-pointer bg-white text-grey400 placeholder:text-grey400 dark:bg-dark dark:text-grey600 dark:placeholder:text-grey600',
							// Validated
							// Hover
							!isInactive &&
								'hover:ring-textSecondaryLight dark:hover:ring-textSecondaryDark',
							// Focus
							!isInactive &&
								'focus:text-textPrimaryLight focus:placeholder-textPrimaryLight focus:ring-textPrimaryLight dark:focus:text-textPrimaryDark dark:focus:placeholder-textPrimaryDark dark:focus:ring-textPrimaryDark',
							// Icon
							'pr-11',
							additionalClasses,
						)}
						placeholder={placeholder}
					/>
					<div
						className={clsx(
							'absolute inset-y-0 right-0 flex items-center pr-3 text-textSecondaryDark group-hover:text-textSecondaryLight dark:text-textSecondaryDark',
						)}
					>
						<SearchIcon />
					</div>
				</div>
				<div className="relative mt-1">
					<div className="mt-1 block items-center">
						<Transition
							show={isOpen}
							as={Fragment}
							leave="transition ease-in duration-100"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<ListboxOptions
								static
								className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-2xl bg-uiPrimaryLight p-4 text-base ring-1 ring-black ring-opacity-5 drop-shadow-menu focus:outline-none dark:bg-textPrimaryLight dark:drop-shadow-menuDark sm:text-sm"
							>
								<div className="relative">
									<div className="sticky top-0 z-20"></div>
									{filteredOptions.map(person => (
										<ListboxOption
											key={person.id}
											className={({ active }) =>
												clsx('relative cursor-default select-none')
											}
											value={person}
										>
											{({ selected, active }) => (
												<>
													{selected ? (
														<ContentTileWithoutImageFallback
															title={person.username || ''}
															description={person.address || ''}
															image={person.avatar || fallbackAvatarImage}
															variant="user"
															isVerified={true}
															showBorder={false}
															showCaret={false}
														/>
													) : (
														<ContentTileWithoutImageFallback
															title={person.username || ''}
															description={person.address || ''}
															image={person.avatar || fallbackAvatarImage}
															variant="user"
															isVerified={true}
															showBorder={false}
															showCaret={true}
														/>
													)}
												</>
											)}
										</ListboxOption>
									))}
									{filteredOptions.length === 0 && (
										<div className="flex h-20 flex-col justify-center pl-5">
											<TypographyLabel size="medium">
												No Results found for search
											</TypographyLabel>
											<TypographyParagraph size="small">
												Please try another or enter artist name and avatar below
											</TypographyParagraph>
										</div>
									)}
								</div>
							</ListboxOptions>
						</Transition>
					</div>
				</div>
			</Listbox>
			{value && (
				<div
					className="group my-2"
					onClick={() => handleUnsetSelected(undefined)}
				>
					<ContentTileWithoutImageFallback
						title={value.username || ''}
						description={value.address || ''}
						image={value.avatar || fallbackAvatarImage}
						variant="user"
						isVerified={true}
						showBorder={false}
						showCaret={false}
						icon={<CloseIcon />}
						handleDelete={() => {}}
						additionalClasses="hover:bg-uiSecondaryLight dark:hover:bg-uiSecondaryDark"
					/>
				</div>
			)}
		</div>
	)
}

export default UserAccountLookupInput
